import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet-async";
import React from "react";
import { Link } from "wouter";
import { ExternalLink, CheckCircle2, Lock } from "lucide-react";

const Help: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Help - Research Tools, Keys & Providers | TSM</title>
        <meta name="description" content="Learn how TSM research tools, BYOK providers, market data keys, archive reports, Best Of, and PDF exports work." />
        <link rel="canonical" href="https://tyl.ee/help" />
      </Helmet>

      <div className="help-wrapper">
        <AppHeader />

        <main className="help-main">
          {/* Hero Section */}
          <section className="help-hero">
            <h1>Help — Research Tools, Keys & Providers</h1>
            <p className="lead">Tylee Smart Markets is free and open for headlines, briefs, monitoring, and saved public research browsing. AI research lanes offer Guest compact previews plus BYOK Short Form and Long Form modes when you want to use your own provider quota.</p>
          </section>

          {/* Free Tier Section */}
          <section className="help-section">
            <div className="section-header">
              <h2>What's Free (No Keys Required)</h2>
              <p>News aggregation and live monitoring are completely free and open.</p>
            </div>

            <div className="feature-grid">
              <div className="feature-card">
                <h3>News Terminal</h3>
                <p>Live headlines from 20+ premium publishers (WSJ, FT, Bloomberg, Reuters, CNBC). Filter by source, region, or category. Updated in real time. No account or credentials required.</p>
              </div>

              <div className="feature-card">
                <h3>Market Brief</h3>
                <p>Plain-English summaries of news that may be relevant to markets or provide financial and economic context. Relevance is assessed from article text; it does not confirm a market reaction. Source links are preserved.</p>
              </div>

              <div className="feature-card">
                <h3>Live Monitor</h3>
                <p>Real-time situational awareness: market TV, oil & energy, rates & dollar moves, policy & conflict, weather & earthquakes, health alerts, and prediction market odds. Intentionally compact and current.</p>
              </div>

              <div className="feature-card">
                <h3>System Status</h3>
                <p>Live health diagnostics for the web shell, TTMA engine, storage paths, deploy marker, report archive, and operator-only logs. Updated every 15 seconds.</p>
              </div>

              <div className="feature-card">
                <h3>Research Archive</h3>
                <p>Your saved reports can be reopened, downloaded as Markdown or PDF, and printed from the same browser or signed-in profile. Only Featured / Best Of reports are public to everyone.</p>
              </div>

              <div className="feature-card">
                <h3>Market Radar</h3>
                <p>Adaptive observation of, at the time of writing, roughly 785 crypto markets. Radar allocates richer real-time instrumentation to markets that earn its attention, so movement through its attention ranking becomes its own signal. Free to view, no credentials required. <Link href="/radar/guide">How Radar works</Link>.</p>
              </div>
            </div>
          </section>

          {/* Research Tools Section */}
          <section className="help-section">
            <div className="section-header">
              <h2>Research Tools</h2>
              <p>Three research lanes support Guest compact, BYOK Short Form, and BYOK Long Form prompts.</p>
            </div>

            <p className="section-intro">
              <strong>Digital Asset Trader</strong> is the short-term crypto workflow powered by TTMA's eight-domain engine. <strong>TradFi Research</strong> creates institutional reports for equities, commodities, indexes, rates, and macro proxies. <strong>Digital Assets Research</strong> creates institutional crypto and token research. Finished reports support Markdown, PDF, print, email, archive, and Best Of curation.
            </p>
            <p className="section-intro">
              Asset search is lane-aware: Digital Asset Trader stays limited to Bitget-supported tradable instruments, while TradFi and Digital Assets Research also accept Yahoo Finance symbols and friendly aliases such as GOLD, XAU, XAUT, PAXG, SILVER, SPX, NVDA, F, BTC, and ETH.
            </p>

            <div className="accordion">
              <details className="accordion-item">
                <summary className="accordion-header">LLM Providers (Required for Analysis)</summary>
                <div className="accordion-content">
                  <p>TSM uses LLM APIs to generate analysis and synthesis. Guest compact can preview research when shared capacity is available. BYOK Short Form and Long Form require your own key for the selected provider.</p>

                  <div className="provider-list">
                    <div className="provider">
                      <h4>Groq</h4>
                      <p><strong>What it does:</strong> Fast LLM inference for analysis synthesis. Groq specializes in low-latency completions.</p>
                      <p><strong>What it unlocks:</strong> Digital Asset Trader plus Groq-backed TradFi and Digital Assets institutional research.</p>
                      <p><strong>Free tier:</strong> Yes (limited quota). <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer">Get a Groq API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>Google Gemini</h4>
                      <p><strong>What it does:</strong> Grounded Google Search research for TradFi and Digital Assets institutional research.</p>
                      <p><strong>What it unlocks:</strong> Gemini remains selectable. Search-grounded Analytics requests are blocked before provider use under TSM's free-only policy.</p>
                      <p><strong>Free tier:</strong> Gemini text access depends on model and account. Google Search grounding required by these Analytics lanes is unavailable under TSM's free-only policy. <a href="https://ai.google.dev" target="_blank" rel="noopener noreferrer">Gemini API details <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>OpenRouter</h4>
                      <p><strong>What it does:</strong> Routes Analytics research through one key to OpenRouter's free model pool by default.</p>
                      <p><strong>What it unlocks:</strong> Digital Asset Trader, TradFi, and Digital Assets research. The requested router is `openrouter/free`; the resolved model is shown with the result.</p>
                      <p><strong>Free tier:</strong> Uses only the free GPT-OSS model and free-model router unless you explicitly change the env. <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">Get an OpenRouter API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                  </div>
                </div>
              </details>

              <details className="accordion-item">
                <summary className="accordion-header">Market Data Providers (Optional, Enhances Analysis)</summary>
                <div className="accordion-content">
                  <p>Market data providers enrich TTMA with historical prices, volatility, options data, and fundamental metrics. Optional but recommended for complete analysis.</p>

                  <div className="provider-list">
                    <div className="provider">
                      <h4>Finnhub</h4>
                      <p><strong>What it does:</strong> Stock quotes, basic financial metrics, earnings calendar data, news, insider trades, and ownership data.</p>
                      <p><strong>Enhances:</strong> Valuation, EPS, earnings timing, and fundamental analysis domains in TTMA.</p>
                      <p><strong>Free tier:</strong> Yes. <a href="https://finnhub.io" target="_blank" rel="noopener noreferrer">Get a Finnhub API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>Alpha Vantage</h4>
                      <p><strong>What it does:</strong> Time-series stock prices, technical indicators, forex data, company overview fundamentals, and earnings schedules.</p>
                      <p><strong>Enhances:</strong> P/E, EPS, sector, industry, target-price, technical, and price-level domains.</p>
                      <p><strong>Free tier:</strong> TSM allows up to 25 calls per rolling 24 hours across its active consumers. <a href="https://www.alphavantage.co" target="_blank" rel="noopener noreferrer">Get an Alpha Vantage API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>Polygon</h4>
                      <p><strong>What it does:</strong> Real-time and historical market data, aggregates, options chains, technical indicators, and a keyed active ticker reference universe.</p>
                      <p><strong>Enhances:</strong> Order book, derivatives, technical analysis, and symbol-discovery workflows when a Polygon key is available.</p>
                      <p><strong>Free tier:</strong> Limited. <a href="https://polygon.io" target="_blank" rel="noopener noreferrer">Get a Polygon API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>CoinMarketCap</h4>
                      <p><strong>What it does:</strong> Cryptocurrency prices, market cap, volume, metadata for 10,000+ cryptos.</p>
                      <p><strong>Enhances:</strong> Crypto analysis and DeFi domain.</p>
                      <p><strong>Free tier:</strong> Yes (limited). <a href="https://coinmarketcap.com/api" target="_blank" rel="noopener noreferrer">Get a CoinMarketCap API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>FRED (Federal Reserve)</h4>
                      <p><strong>What it does:</strong> US economic data: inflation, unemployment, yields, GDP, Treasury rates, and Fed funds series.</p>
                      <p><strong>Enhances:</strong> Macro context domain with authoritative rates and policy backdrop data.</p>
                      <p><strong>Free tier:</strong> Yes. <a href="https://fred.stlouisfed.org/docs/api" target="_blank" rel="noopener noreferrer">Get a FRED API key <ExternalLink size={14} style={{display: 'inline'}} aria-hidden="true" /></a></p>
                    </div>

                    <div className="provider">
                      <h4>Other Providers</h4>
                      <p>Yahoo Finance supports default mainstream symbol search and public chart snapshots. Financial Modeling Prep (fundamentals) and EIA (energy data) add depth to specific domains. NewsAPI's Developer plan is not available for production use under TSM's free-only policy; local RSS remains the news alternative.</p>
                    </div>
                  </div>
                </div>
              </details>
            </div>
          </section>

          {/* Prompt Modes */}
          <section className="help-section">
            <div className="section-header">
              <h2>Research Prompt Modes</h2>
              <p>Choose the mode based on access, quota, and depth.</p>
            </div>

            <div className="mode-grid">
              <div className="mode-card">
                <h3>Guest compact</h3>
                <p>No LLM key required. Uses smaller prompts and limited shared capacity when available. Best for trying a lane before adding keys.</p>
                <span>No saved LLM key is sent.</span>
              </div>

              <div className="mode-card">
                <h3>BYOK Short Form</h3>
                <p>Default mode for users with saved provider keys. Uses the selected Groq or OpenRouter key with a smaller prompt. Gemini Search requests are blocked under TSM's free-only policy.</p>
                <span>Good first run for most symbols.</span>
              </div>

              <div className="mode-card">
                <h3>BYOK Long Form</h3>
                <p>Deepest research mode. Uses your selected provider key with the full prompt, so it can take longer and consume more quota.</p>
                <span>Use when depth matters more than speed.</span>
              </div>
            </div>

            <p className="section-intro">
              Sign-in is optional for local BYOK use. Signing in lets you sync an encrypted browser vault and reopen profile-tied archives across devices, but provider usage still runs through the keys and limits attached to your chosen provider account.
            </p>
          </section>

          {/* Feature Comparison */}
          <section className="help-section">
            <div className="section-header">
              <h2>Feature Comparison: Free vs. Guest vs. BYOK</h2>
            </div>

            <div className="comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free Tier</th>
                    <th>Guest Mode</th>
                    <th>BYOK Short / Long</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>News Terminal</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                  </tr>
                  <tr>
                    <td>Market Brief</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                  </tr>
                  <tr>
                    <td>Live Monitor</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Yes</td>
                  </tr>
                  <tr>
                    <td>Digital Asset Trader</td>
                    <td><Lock size={18} className="icon-no" aria-hidden="true" /> No</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Compact preview</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Short or full 8-domain prompt</td>
                  </tr>
                  <tr>
                    <td>TradFi &amp; Digital Assets Research</td>
                    <td><Lock size={18} className="icon-no" aria-hidden="true" /> No</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Compact preview</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Short or long institutional prompt</td>
                  </tr>
                  <tr>
                    <td>Archive, Best Of &amp; PDF export</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> View</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Limited</td>
                    <td><CheckCircle2 size={18} className="icon-yes" aria-hidden="true" /> Create + export</td>
                  </tr>
                  <tr>
                    <td>API Key Storage</td>
                    <td>—</td>
                    <td>No browser LLM key required</td>
                    <td>Browser vault, optional encrypted profile sync</td>
                  </tr>
                  <tr>
                    <td>Rate Limits</td>
                    <td>—</td>
                    <td>Tight (shared)</td>
                    <td>Your provider limits</td>
                  </tr>
                  <tr>
                    <td>Cost to You</td>
                    <td>Free</td>
                    <td>Free (limited)</td>
                    <td>Pay providers directly</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* BYOK Explained */}
          <section className="help-section">
            <div className="section-header">
              <h2>How BYOK Works</h2>
            </div>

            <p>BYOK (Bring Your Own Key) means:</p>

            <ol className="steps">
              <li><strong>You provide API keys.</strong> Add your own credentials from providers like Groq, Gemini, OpenRouter, Finnhub, and Alpha Vantage in the "Keys & Help" section.</li>
              <li><strong>Keys live in your browser by default.</strong> Your credentials are stored in your browser's local vault unless you opt in to encrypted profile sync.</li>
              <li><strong>Keys are forwarded only when needed.</strong> When you request research, your keys are forwarded directly to the provider you choose and only for that specific request.</li>
              <li><strong>Prompt mode controls cost and depth.</strong> Short Form uses a smaller prompt. Long Form uses the full prompt and can consume more provider quota.</li>
              <li><strong>You pay providers.</strong> API calls are billed directly to your account with each provider. TSM stores an encrypted recovery copy only when you explicitly choose profile sync after signing in.</li>
              <li><strong>You control access.</strong> Revoke keys with the provider and remove them from your browser vault. If you use profile sync, sync the updated vault to replace its encrypted recovery copy.</li>
            </ol>

            <div className="byok-benefit">
              <strong>Why BYOK?</strong> External LLM APIs (Groq, Gemini, OpenRouter) and market-data providers have real usage costs. BYOK keeps those costs with you rather than subsidizing them for everyone, making the free tier genuinely free and sustainable.
            </div>
          </section>

          {/* Getting Started */}
          <section className="help-section">
            <div className="section-header">
              <h2>Getting Started</h2>
            </div>

            <div className="cta-group">
              <div className="cta-card">
                <h3>Try Research Without Keys</h3>
                <p>Use Guest compact to preview research with limited server-side capacity. No credentials needed, but with tighter rate limits.</p>
                <Link href="/analytics" className="cta-link">Go to Analytics →</Link>
              </div>

              <div className="cta-card">
                <h3>Add Your Own Keys</h3>
                <p>Add LLM and market-data keys, then choose BYOK Short Form for lighter runs or BYOK Long Form for deeper reports.</p>
                <Link href="/get-started" className="cta-link">Manage Keys →</Link>
              </div>

              <div className="cta-card">
                <h3>Learn More</h3>
                <p>Have questions? Check the FAQ for common questions about pricing, providers, and features.</p>
                <Link href="/faq" className="cta-link">Read FAQ →</Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>

      <style>{`
        .help-wrapper {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background: var(--bg-primary, #0a0e27);
          color: var(--text-primary, #e0e0e0);
        }

        .help-main {
          flex: 1;
          max-width: 1000px;
          margin: 0 auto;
          width: 100%;
          padding: 40px 20px;
        }

        .help-hero {
          margin-bottom: 60px;
          text-align: center;
        }

        .help-hero h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 20px;
          color: var(--text-primary, #fff);
        }

        .help-hero .lead {
          font-size: 1.1rem;
          color: var(--text-secondary, #bbb);
          line-height: 1.6;
          max-width: 700px;
          margin: 0 auto;
        }

        .help-section {
          margin-bottom: 50px;
        }

        .section-header {
          margin-bottom: 30px;
        }

        .section-header h2 {
          font-size: 1.8rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: var(--text-primary, #fff);
        }

        .section-header p {
          color: var(--text-secondary, #999);
          font-size: 1rem;
        }

        .section-intro {
          font-size: 1.05rem;
          line-height: 1.6;
          color: var(--text-secondary, #bbb);
          margin-bottom: 25px;
        }

        .feature-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .feature-card {
          border: 1px solid var(--border-color, #333);
          border-radius: 8px;
          background: var(--bg-secondary, #111729);
          padding: 25px;
        }

        .feature-card h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: var(--text-primary, #fff);
        }

        .feature-card p {
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--text-secondary, #999);
        }

        .accordion {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .accordion-item {
          border: 1px solid var(--border-color, #333);
          border-radius: 8px;
          background: var(--bg-secondary, #111729);
          overflow: hidden;
        }

        .accordion-header {
          cursor: pointer;
          padding: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 600;
          font-size: 1.05rem;
          transition: background-color 0.2s, box-shadow 0.2s;
          user-select: none;
          outline: none;
        }

        .accordion-header:hover {
          background-color: var(--bg-hover, #1a2038);
        }

        .accordion-header:focus-visible {
          background-color: var(--bg-hover, #1a2038);
          box-shadow: inset 0 0 0 2px #4a9eff;
        }

        .cta-link:focus-visible,
        .provider a:focus-visible {
          outline: 2px solid #4a9eff;
          outline-offset: 2px;
          border-radius: 2px;
        }

        .accordion-header::after {
          content: '▼';
          font-size: 0.8rem;
          transition: transform 0.2s;
          margin-left: 15px;
          flex-shrink: 0;
        }

        details[open] .accordion-header::after {
          transform: rotate(180deg);
        }

        .accordion-content {
          padding: 0 20px 20px 20px;
          background: var(--bg-secondary, #0f1522);
        }

        .provider-list {
          display: flex;
          flex-direction: column;
          gap: 25px;
          margin-top: 20px;
        }

        .provider {
          padding: 20px;
          border-left: 3px solid var(--accent, #4a9eff);
          background: var(--bg-primary, #0a0e27);
          border-radius: 4px;
        }

        .provider h4 {
          font-size: 1.05rem;
          font-weight: 600;
          margin-bottom: 8px;
          color: var(--text-primary, #fff);
        }

        .provider p {
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--text-secondary, #999);
          margin-bottom: 8px;
        }

        .provider a {
          color: var(--accent, #4a9eff);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .provider a:hover {
          text-decoration: underline;
        }

        .mode-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .mode-card {
          border: 1px solid var(--border-color, #333);
          border-radius: 8px;
          background: var(--bg-secondary, #111729);
          padding: 20px;
        }

        .mode-card h3 {
          font-size: 1.05rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: var(--text-primary, #fff);
        }

        .mode-card p {
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--text-secondary, #999);
          margin-bottom: 12px;
        }

        .mode-card span {
          display: inline-flex;
          color: var(--accent, #4a9eff);
          font-size: 0.85rem;
          font-weight: 700;
        }

        .comparison-table {
          overflow-x: auto;
          margin-bottom: 30px;
          border-radius: 8px;
          border: 1px solid var(--border-color, #333);
        }

        .comparison-table table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-secondary, #111729);
        }

        .comparison-table th {
          background: var(--bg-hover, #1a2038);
          padding: 15px;
          text-align: left;
          font-weight: 600;
          border-bottom: 1px solid var(--border-color, #333);
        }

        .comparison-table td {
          padding: 15px;
          border-bottom: 1px solid var(--border-color, #333);
          font-size: 0.95rem;
        }

        .comparison-table tbody tr:last-child td {
          border-bottom: none;
        }

        .comparison-table td:first-child {
          font-weight: 600;
        }

        .icon-yes {
          color: #4ade80;
          display: inline;
        }

        .icon-no {
          color: #ef4444;
          display: inline;
        }

        .steps {
          list-style: decimal;
          padding-left: 25px;
          margin: 20px 0;
          font-size: 1rem;
          line-height: 1.8;
          color: var(--text-secondary, #bbb);
        }

        .steps li {
          margin-bottom: 12px;
        }

        .steps strong {
          color: var(--text-primary, #fff);
        }

        .byok-benefit {
          margin-top: 20px;
          padding: 20px;
          border-left: 4px solid var(--accent, #4a9eff);
          background: var(--bg-secondary, #111729);
          border-radius: 4px;
          color: var(--text-secondary, #bbb);
        }

        .cta-group {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .cta-card {
          border: 1px solid var(--border-color, #333);
          border-radius: 8px;
          background: var(--bg-secondary, #111729);
          padding: 25px;
          display: flex;
          flex-direction: column;
        }

        .cta-card h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 12px;
          color: var(--text-primary, #fff);
        }

        .cta-card p {
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--text-secondary, #999);
          margin-bottom: 15px;
          flex: 1;
        }

        .cta-link {
          color: var(--accent, #4a9eff);
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
          align-self: flex-start;
        }

        .cta-link:hover {
          color: var(--accent-light, #6bb8ff);
        }

        @media (max-width: 768px) {
          .help-main {
            padding: 20px 15px;
          }

          .help-hero h1 {
            font-size: 2rem;
          }

          .help-hero .lead {
            font-size: 1rem;
          }

          .section-header h2 {
            font-size: 1.5rem;
          }

          .feature-grid {
            grid-template-columns: 1fr;
          }

          .mode-grid {
            grid-template-columns: 1fr;
          }

          .comparison-table {
            font-size: 0.85rem;
          }

          .comparison-table th,
          .comparison-table td {
            padding: 12px 8px;
          }
        }
      `}</style>
    </>
  );
};

export default Help;
